import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAvailabilityTokenByToken, getVolunteer, saveVolunteerAvailability, markAvailabilityTokenUsed } from '../services/firebase';

const DAYS = [
  { key: 'thu', label: 'Thursday, April 9',   field: 'thuAvailability' },
  { key: 'fri', label: 'Friday, April 10',    field: 'friAvailability' },
  { key: 'sat', label: 'Saturday, April 11',  field: 'satAvailability' },
  { key: 'sun', label: 'Sunday, April 12',    field: 'sunAvailability' },
];

const SHIFTS_BY_DAY = {
  thuAvailability: ['7-10 AM', '10 AM-1 PM', '1-3 PM', '3-5 PM', '5-8 PM'],
  friAvailability: ['7-10 AM', '10 AM-1 PM', '1-3 PM', '3-5 PM', '5-8 PM'],
  satAvailability: ['7-10 AM', '10 AM-1 PM', '1-3 PM', '3-5 PM', '5-8 PM'],
  sunAvailability: ['7-10 AM', '10 AM-1 PM', '1-5 PM', '5-8 PM'],
};

// Normalize stored value to an array for the checkbox UI
function toArray(val, dayField) {
  if (!val || val === '' || val === 'Not Available') return [];
  if (val === 'Anytime') return [...(SHIFTS_BY_DAY[dayField] || [])];
  if (Array.isArray(val)) return val;
  return [];
}

// Determine the "quick select" mode for display
function getMode(val) {
  if (!val || val === '' || val === 'Not Available') return 'unavailable';
  if (val === 'Anytime') return 'anytime';
  return 'specific';
}

function AvailabilityResponse() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading | invalid | form | submitted
  const [tokenDoc, setTokenDoc] = useState(null);
  const [volunteer, setVolunteer] = useState(null);

  // Per-day availability: 'Not Available' | 'Anytime' | string[]
  const [availability, setAvailability] = useState({
    thuAvailability: 'Not Available',
    friAvailability: 'Not Available',
    satAvailability: 'Not Available',
    sunAvailability: 'Not Available',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = searchParams.get('t');
    if (!token) { setStatus('invalid'); return; }

    (async () => {
      const td = await getAvailabilityTokenByToken(token);
      if (!td || td.used) { setStatus('invalid'); return; }

      const vol = await getVolunteer(td.volunteerId);
      if (!vol) { setStatus('invalid'); return; }

      setTokenDoc(td);
      setVolunteer(vol);

      setAvailability({
        thuAvailability: vol.thuAvailability || 'Not Available',
        friAvailability: vol.friAvailability || 'Not Available',
        satAvailability: vol.satAvailability || 'Not Available',
        sunAvailability: vol.sunAvailability || 'Not Available',
      });

      setStatus('form');
    })();
  }, [searchParams]);

  const setDayMode = (field, mode) => {
    setAvailability(prev => ({
      ...prev,
      [field]: mode === 'unavailable' ? 'Not Available'
             : mode === 'anytime'     ? 'Anytime'
             : [], // specific — user checks individual boxes
    }));
  };

  const toggleShift = (field, shift) => {
    setAvailability(prev => {
      const current = toArray(prev[field]);
      const updated = current.includes(shift)
        ? current.filter(s => s !== shift)
        : [...current, shift];
      return { ...prev, [field]: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await saveVolunteerAvailability(tokenDoc.volunteerId, availability);
    await markAvailabilityTokenUsed(tokenDoc.token);
    setStatus('submitted');
    setSubmitting(false);
  };

  if (status === 'loading') {
    return <div className="avail-response-page"><p>Loading…</p></div>;
  }

  if (status === 'invalid') {
    return (
      <div className="avail-response-page">
        <div style={{ background: '#1e293b', color: 'white', padding: '20px 28px', borderRadius: '8px 8px 0 0' }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>SMPW Masters Event 2026</h2>
          <p style={{ margin: '4px 0 0', opacity: 0.75, fontSize: '14px' }}>Augusta, Georgia — April 9–12, 2026</p>
        </div>
        <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', padding: '28px', borderRadius: '0 0 8px 8px' }}>
          <p style={{ color: '#dc2626', fontWeight: 600 }}>This link is invalid or has already been used.</p>
          <p style={{ color: '#64748b', fontSize: '14px' }}>If you believe this is an error, please contact your scheduling coordinator.</p>
        </div>
      </div>
    );
  }

  if (status === 'submitted') {
    return (
      <div className="avail-response-page">
        <div style={{ background: '#1e293b', color: 'white', padding: '20px 28px', borderRadius: '8px 8px 0 0' }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>SMPW Masters Event 2026</h2>
          <p style={{ margin: '4px 0 0', opacity: 0.75, fontSize: '14px' }}>Augusta, Georgia — April 9–12, 2026</p>
        </div>
        <div className="avail-thankyou" style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
          <h3 style={{ color: '#166534', marginBottom: '8px' }}>Thank You, {volunteer?.firstName}!</h3>
          <p style={{ color: '#15803d' }}>Your availability has been submitted successfully. We'll be in touch with your assignment details.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="avail-response-page">
      <div style={{ background: '#1e293b', color: 'white', padding: '20px 28px', borderRadius: '8px 8px 0 0' }}>
        <h2 style={{ margin: 0, fontSize: '20px' }}>SMPW Masters Event 2026</h2>
        <p style={{ margin: '4px 0 0', opacity: 0.75, fontSize: '14px' }}>Augusta, Georgia — April 9–12, 2026</p>
      </div>

      <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', padding: '28px', borderRadius: '0 0 8px 8px' }}>
        <h3 style={{ color: '#2563eb', marginTop: 0 }}>Submit Your Availability</h3>
        <p>Hi {volunteer?.firstName}, please select your availability for each day of the event below.</p>

        <form onSubmit={handleSubmit}>
          {DAYS.map(day => {
            const mode = getMode(availability[day.field]);
            const selected = toArray(availability[day.field], day.field);
            return (
              <div className="avail-day-row" key={day.key}>
                <div className="avail-day-label">{day.label}</div>

                <div className="avail-quick-row">
                  <button
                    type="button"
                    className={`avail-quick-btn${mode === 'unavailable' ? ' active-unavail' : ''}`}
                    onClick={() => setDayMode(day.field, 'unavailable')}
                  >
                    Not Available
                  </button>
                  <button
                    type="button"
                    className={`avail-quick-btn${mode === 'anytime' ? ' active-avail' : ''}`}
                    onClick={() => setDayMode(day.field, 'anytime')}
                  >
                    Anytime
                  </button>
                  <button
                    type="button"
                    className={`avail-quick-btn${mode === 'specific' ? ' active-specific' : ''}`}
                    onClick={() => setDayMode(day.field, 'specific')}
                  >
                    Select shifts…
                  </button>
                </div>

                {(mode === 'specific' || mode === 'anytime') && (
                  <div className="avail-checkbox-grid">
                    {(SHIFTS_BY_DAY[day.field] || []).map(shift => (
                      <label key={shift} className="avail-check-item">
                        <input
                          type="checkbox"
                          checked={selected.includes(shift)}
                          onChange={() => {
                            if (mode === 'anytime') {
                              // Switch to specific, deselect just this one
                              setAvailability(prev => ({ ...prev, [day.field]: (SHIFTS_BY_DAY[day.field] || []).filter(s => s !== shift) }));
                            } else {
                              toggleShift(day.field, shift);
                            }
                          }}
                        />
                        {shift}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <button type="submit" className="avail-submit-btn" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit My Availability'}
          </button>
        </form>

        <p style={{ marginTop: '20px', color: '#64748b', fontSize: '12px' }}>
          SMPW Scheduling Team – Augusta Masters
        </p>
      </div>
    </div>
  );
}

export default AvailabilityResponse;
